import {
  General,
  Users,
  Alerts,
  Medical,
  MediaDevices,
  Finance,
} from 'icons/untitledui';

import { ExtractionClass, ExtractedEntity } from '../_types';

export type IconComponent = typeof General.InfoCircle;

export interface ExtractionClassConfig {
  icon: IconComponent;
}

const extractionClassIconMap: Record<string, ExtractionClassConfig> = {
  person: { icon: Users.Users03 },
  department: { icon: General.MarkerPin01 },
  risk: { icon: Alerts.AlertTriangle },
  urgency: { icon: Alerts.AlertCircle },
  health_and_safety_risk: { icon: General.ActivityHeart },
  operational_risk: { icon: General.Settings01 },
  inventory_risk: { icon: General.Package },
  supply_chain_risk: { icon: General.Truck01 },
  equipment_risk: { icon: General.Tool01 },
  employee_relations_risk: { icon: Users.Users03 },
  compliance_risk: { icon: General.ClipboardCheck },
  reputational_risk: { icon: General.Award03 },
  financial_risk: { icon: Finance.BankNote03 },
  security_risk: { icon: General.ShieldTick },
  temporal_state: { icon: General.Clock },
  location: { icon: General.Building02 },
  equipment: { icon: General.Tool01 },
  incident: { icon: Alerts.AlertCircle },
  incident_outcome: { icon: Alerts.AlertCircle },
  injury: { icon: Medical.MedicalCross },
  action: { icon: General.Check },
  follow_up_signal: { icon: MediaDevices.LightBulb05 },
  labor_relations_risk: { icon: General.Briefcase01 },
};

const DEFAULT_CONFIG: ExtractionClassConfig = {
  icon: General.Tag01,
};

export function getConfigForExtractionClass(
  extractionClass: ExtractionClass
): ExtractionClassConfig {
  const key = extractionClass.toLowerCase().replace(/\s+/g, '_');
  return extractionClassIconMap[key] || DEFAULT_CONFIG;
}

export function getIconForExtractionClass(
  extractionClass: ExtractionClass
): IconComponent {
  return getConfigForExtractionClass(extractionClass).icon;
}

export function formatCategoryLabel(category: string): string {
  return category
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export const HIDDEN_ENTITY_CLASSES = [
  'action',
  'incident_outcome',
  'location',
  'person',
  'temporal_state',
  'urgency',
];

export const VISIBLE_ENTITY_TAG_CLASSES = [
  'Compliance Risk',
  'Equipment Risk',
  'Health and Safety Risk',
  'Incident',
  'Inventory Risk',
  'Operational Risk',
  'Supply Chain Risk',
];

export interface FlattenedTag {
  key: string;
  extractionClass: ExtractionClass;
  category: string;
  description: string | null;
}

export function filterExtractions(
  extractions: ExtractedEntity[],
  { hideRisk = false }: { hideRisk?: boolean } = {}
): ExtractedEntity[] {
  return extractions.filter((e) => {
    const classKey = e.extraction_class_key.toLowerCase().replace(/\s+/g, '_');
    if (HIDDEN_ENTITY_CLASSES.includes(classKey)) return false;
    if (hideRisk && classKey === 'risk') return false;
    return true;
  });
}

export function flattenExtractions(
  extractions: ExtractedEntity[]
): FlattenedTag[] {
  return extractions.flatMap((extraction) => {
    const classKey = extraction.extraction_class_key
      .toLowerCase()
      .replace(/\s+/g, '_');
    const useClassAsLabel = classKey === 'incident' || classKey === 'injury';

    if (useClassAsLabel) {
      return [
        {
          key: extraction.id,
          extractionClass: extraction.extraction_class,
          category: extraction.extraction_class,
          description: extraction.description,
        },
      ];
    }

    const categories = extraction.attributes?.categories || [];
    if (categories.length === 0) {
      return [
        {
          key: extraction.id,
          extractionClass: extraction.extraction_class,
          category: extraction.extraction_text,
          description: extraction.description,
        },
      ];
    }

    return categories.map((category, index) => ({
      key: `${extraction.id}-${index}`,
      extractionClass: extraction.extraction_class,
      category,
      description: extraction.description,
    }));
  });
}

export function deduplicateTags(tags: FlattenedTag[]): FlattenedTag[] {
  const seen = new Set<string>();
  return tags.filter((tag) => {
    const deduplicationKey = `${tag.extractionClass}::${tag.category}`;
    if (seen.has(deduplicationKey)) return false;
    seen.add(deduplicationKey);
    return true;
  });
}

export function collectVisibleEntityTags(
  extractions: ExtractedEntity[]
): FlattenedTag[] {
  return deduplicateTags(
    flattenExtractions(filterExtractions(extractions, { hideRisk: true }))
  );
}
